import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, MagnifyingGlass, QrCode, PencilSimple, Trash, Wrench, Scan, Printer, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATEGORIES, CONDITIONS, STATUSES, CRITICALITY, iconFor, gbp, titleCase } from "../components/assets/assetUtils";
import { ConditionBadge, StatusBadge, CriticalityBadge, ServiceBadge } from "../components/assets/AssetBadges";
import { AssetFormDialog } from "../components/assets/AssetFormDialog";
import { AssetScanDialog } from "../components/assets/AssetScanDialog";
import { AssetDuePanel, CapitalPlanPanel } from "../components/assets/AssetPanels";
import { ManualImportDialog } from "../components/assets/ManualImportDialog";
import { AssetHealthBadge } from "../components/assets/AssetHealthBadge";
import { AssetHealthPanel } from "../components/assets/AssetHealthPanel";

const WRITE_ROLES = ["super_admin", "admin", "project_manager"];
const DUE_CHIPS = [["", "All"], ["soon", "Due soon"], ["overdue", "Overdue"], ["warranty", "Warranty"], ["eol", "End of life"]];
const REC_CHIPS = [["replace_now", "Replace now"], ["plan_replacement", "Plan replacement"]];

export default function AssetsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = WRITE_ROLES.includes(user?.role);
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category: "all", status: "all", condition: "all", criticality: "all", due: "", recommendation: "" });
  const [tab, setTab] = useState("register");
  const [formOpen, setFormOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [selected, setSelected] = useState([]);

  const fetchAssets = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const res = await API.get("/assets", { params });
      setAssets(res.data);
    } catch { /* keep previous */ }
    setLoading(false);
  }, [search, filters]);
  const fetchStats = useCallback(() => API.get("/assets/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  const refresh = () => { fetchAssets(); fetchStats(); };

  useEffect(() => { const t = setTimeout(fetchAssets, 200); return () => clearTimeout(t); }, [fetchAssets]);
  useEffect(() => { fetchStats(); API.get("/assets/options").then((r) => setOptions(r.data)).catch(() => {}); }, [fetchStats]);

  const handleDelete = async () => {
    try { await API.delete(`/assets/${del.id}`); toast.success("Asset deleted"); setDel(null); refresh(); } catch { toast.error("Failed to delete asset"); }
  };
  const printLabels = async () => {
    try {
      const { data } = await API.post("/assets/bulk-qr", { asset_ids: selected });
      const win = window.open("", "_blank");
      if (!win) return toast.error("Popup blocked");
      win.document.write(`<html><head><title>Asset labels</title><style>body{margin:16px;font-family:sans-serif}img{width:360px;border:1px dashed #999;margin:6px;page-break-inside:avoid}</style></head><body>${data.labels.map((l) => `<img src="data:image/png;base64,${l.qr_base64}" alt="${l.name}"/>`).join("")}<script>window.onload=()=>window.print()</script></body></html>`);
      win.document.close();
    } catch { toast.error("Couldn't build labels"); }
  };
  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="space-y-4" data-testid="assets-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo]">Asset register</h1>
          <p className="text-sm text-muted-foreground">Every piece of kit, its value, its service clock and its history — tagged and scannable.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="asset-scan-btn"><Scan size={16} className="mr-1" /> Scan tag</Button>
          {canEdit && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="asset-import-btn"><Sparkle size={16} className="mr-1 text-primary" /> Import from manual</Button>}
          {canEdit && <Button onClick={() => { setEditAsset(null); setFormOpen(true); }} data-testid="asset-add-btn"><Plus size={16} className="mr-1" /> Add asset</Button>}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="asset-stats">
          <Stat label="Assets" value={stats.total} sub={`${stats.active} active`} testid="asset-stat-total" />
          <Stat label="Service overdue" value={stats.service_overdue} color={stats.service_overdue ? "text-red-600" : ""} onClick={() => { setTab("register"); setF("due", "overdue"); }} testid="asset-stat-overdue" />
          <Stat label="Due soon" value={stats.service_due_soon} color={stats.service_due_soon ? "text-amber-600" : ""} onClick={() => { setTab("register"); setF("due", "soon"); }} testid="asset-stat-due-soon" />
          <Stat label="Warranty expiring" value={stats.warranty_expiring} sub={`${stats.warranty_expired} expired`} color={stats.warranty_expiring ? "text-orange-600" : ""} onClick={() => { setTab("register"); setF("due", "warranty"); }} testid="asset-stat-warranty" />
          <Stat label="Book value" value={gbp(stats.book_value_total)} sub={`${gbp(stats.purchase_cost_total)} at cost`} testid="asset-stat-book-value" />
          <Stat label="End of life ≤ 12m" value={stats.eol_within_12m} color={stats.eol_within_12m ? "text-amber-600" : ""} onClick={() => setTab("capital")} testid="asset-stat-eol" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="register" data-testid="asset-tab-register">Register</TabsTrigger>
          <TabsTrigger value="due" data-testid="asset-tab-due">Due for service</TabsTrigger>
          <TabsTrigger value="health" data-testid="asset-tab-health">Health &amp; CAPEX</TabsTrigger>
          {canEdit && <TabsTrigger value="capital" data-testid="asset-tab-capital">Capital plan</TabsTrigger>}
        </TabsList>
      </Tabs>

      {tab === "due" && <AssetDuePanel />}
      {tab === "health" && <AssetHealthPanel options={options} />}
      {tab === "capital" && <CapitalPlanPanel />}
      {tab === "register" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, tag, serial, model, property…" className="pl-9" data-testid="asset-search" />
            </div>
            <Sel value={filters.category} onChange={(v) => setF("category", v)} all="All categories" items={CATEGORIES} testid="asset-filter-category" />
            <Sel value={filters.status} onChange={(v) => setF("status", v)} all="All statuses" items={STATUSES} fmt={titleCase} testid="asset-filter-status" />
            <Sel value={filters.condition} onChange={(v) => setF("condition", v)} all="All conditions" items={CONDITIONS} fmt={titleCase} testid="asset-filter-condition" />
            <Sel value={filters.criticality} onChange={(v) => setF("criticality", v)} all="Any criticality" items={CRITICALITY} fmt={titleCase} testid="asset-filter-criticality" />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {DUE_CHIPS.map(([v, l]) => <button key={v} type="button" onClick={() => setF("due", v)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filters.due === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`asset-chip-${v || "all"}`}>{l}</button>)}
            <span className="h-4 w-px bg-border mx-1" />
            {REC_CHIPS.map(([v, l]) => <button key={v} type="button" onClick={() => setF("recommendation", filters.recommendation === v ? "" : v)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filters.recommendation === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`asset-chip-${v}`}>{l}</button>)}
            {selected.length > 0 && <Button size="sm" variant="outline" className="ml-auto" onClick={printLabels} data-testid="asset-print-labels"><Printer size={14} className="mr-1" /> Print {selected.length} label{selected.length === 1 ? "" : "s"}</Button>}
          </div>

          {loading ? <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
            : assets.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="assets-empty">
                <Wrench size={40} className="mx-auto text-muted-foreground mb-3" />
                <h3 className="font-semibold mb-1">No assets match</h3>
                <p className="text-sm text-muted-foreground mb-4">{search || filters.due ? "Try clearing the search or filters." : "Register your first asset to start tracking value, services and history."}</p>
                {canEdit && !search && !filters.due && <Button onClick={() => { setEditAsset(null); setFormOpen(true); }}><Plus size={16} className="mr-1" /> Add first asset</Button>}
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-x-auto">
                <table className="w-full text-sm" data-testid="assets-table">
                  <thead><tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    {canEdit && <th className="px-3 py-3 w-8"><input type="checkbox" checked={selected.length > 0 && selected.length === assets.length} onChange={(e) => setSelected(e.target.checked ? assets.map((a) => a.id) : [])} data-testid="asset-select-all" /></th>}
                    <th className="text-left px-4 py-3 font-semibold">Asset</th><th className="text-left px-4 py-3 font-semibold">Where</th><th className="text-left px-4 py-3 font-semibold">Criticality</th>
                    <th className="text-left px-4 py-3 font-semibold">Condition</th><th className="text-left px-4 py-3 font-semibold">Health</th><th className="text-left px-4 py-3 font-semibold">Status</th><th className="text-left px-4 py-3 font-semibold">Next service</th>
                    {canEdit && <th className="text-right px-4 py-3 font-semibold">Book value</th>}<th className="px-4 py-3" />
                  </tr></thead>
                  <tbody className="divide-y divide-border">
                    {assets.map((a) => { const Icon = iconFor(a.category); return (
                      <tr key={a.id} className="row-hover cursor-pointer" onClick={() => navigate(`/assets/${a.id}`)} data-testid={`asset-row-${a.id}`}>
                        {canEdit && <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} /></td>}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <span className="h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0"><Icon size={16} /></span>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{a.name}{a.parent_name && <span className="text-[10px] text-muted-foreground font-normal"> · part of {a.parent_name}</span>}</p>
                              <p className="text-[10px] text-muted-foreground truncate">{a.category}{a.tag_code ? <> · <span className="font-mono">{a.tag_code}</span></> : ""}{a.serial_number ? ` · S/N ${a.serial_number}` : ""}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{a.property_name || "—"}{a.location && <p className="text-[10px]">{a.location}</p>}</td>
                        <td className="px-4 py-3"><CriticalityBadge value={a.criticality} /></td>
                        <td className="px-4 py-3"><ConditionBadge value={a.condition} /></td>
                        <td className="px-4 py-3"><AssetHealthBadge health={a.health} compact testid={`asset-health-${a.id}`} /></td>
                        <td className="px-4 py-3"><StatusBadge value={a.status} /></td>
                        <td className="px-4 py-3"><ServiceBadge asset={a} /></td>
                        {canEdit && <td className="px-4 py-3 text-right font-mono text-xs">{a.purchase_cost > 0 ? gbp(a.book_value) : "—"}</td>}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/assets/${a.id}`)} title="Passport & QR"><QrCode size={14} /></Button>
                            {canEdit && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditAsset(a); setFormOpen(true); }} title="Edit" data-testid={`asset-edit-${a.id}`}><PencilSimple size={14} /></Button>}
                            {["super_admin", "admin"].includes(user?.role) && <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDel(a)} title="Delete" data-testid={`asset-delete-${a.id}`}><Trash size={14} /></Button>}
                          </div>
                        </td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            )}
        </>
      )}

      <AssetFormDialog open={formOpen} asset={editAsset} options={options} onClose={() => { setFormOpen(false); setEditAsset(null); }} onSaved={() => { refresh(); API.get("/assets/options").then((r) => setOptions(r.data)).catch(() => {}); }} />
      <AssetScanDialog open={scanOpen} onClose={() => setScanOpen(false)} />
      <ManualImportDialog open={importOpen} onOpenChange={setImportOpen} options={options} onDone={refresh} />
      <Dialog open={!!del} onOpenChange={() => setDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete asset</DialogTitle><DialogDescription>Delete "{del?.name}" and its service log? Linked jobs are kept. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDel(null)}>Cancel</Button><Button variant="destructive" onClick={handleDelete} data-testid="asset-delete-confirm">Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, sub, color = "", onClick, testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={`border border-border rounded-lg p-3 bg-card text-left ${onClick ? "hover:bg-muted/40 transition-colors" : ""}`} data-testid={testid}>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold font-[Chivo] mt-0.5 ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </Tag>
  );
}

function Sel({ value, onChange, all, items, fmt = (x) => x, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[150px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="all">{all}</SelectItem>{items.map((i) => <SelectItem key={i} value={i}>{fmt(i)}</SelectItem>)}</SelectContent>
    </Select>
  );
}
